import { ReactNode } from 'react';
import { transactionDocuments } from 'src/pages/transactions/constants';
import { TransactionStatus } from 'src/redux/types';
import {
	LogEvent,
	LogStatusEnum,
	LogTypeEnum,
	PropertyTitleCategory,
	TrackerStageName,
	TrackerStepName,
	TrackerStepStatus,
} from 'src/types';

export const trackerSteps: TrackerStepName[] = ['seller', 'property', 'listing', 'buyer', 'legals', 'exchange'];

export const transactionStatus = {
	completed: 'completed',
	error: 'error',
	inProgress: 'inProgress',
	notStarted: 'notStarted',
	pending: 'pending',
} as const;

export const valueByTransactionStatus: Record<TransactionStatus, number> = {
	notStarted: 0,
	completed: 1,
	inProgress: 2,
	pending: 3,
	error: 4,
};

export const transactionStatusIcon: Record<TransactionStatus, ReactNode> = {
	completed: '🟢',
	error: '🔴',
	inProgress: '🟡',
	notStarted: '⚪️',
	pending: '🟠',
} as const;

export const trackerStepStatus: Record<TrackerStepStatus, string> = {
	completed: 'completed',
	error: 'error',
	inProgress: 'inProgress',
	notStarted: 'notStarted',
	pending: 'pending',
};

export const trackerStage = {
	onboarding: 'onboarding',
	questions: 'questions',
	instruction: 'instruction',
	evidence: 'evidence',
	documents: 'documents',
	listing: 'listing',
	searches: 'searches',
	finance: 'finance',
	contractPack: 'contractPack',
	sourceOfFunds: 'sourceOfFunds',
	enquiries: 'enquiries',
	exchange: 'exchange',
} as const;

export const trackerStages = Object.values(trackerStage);

export const labelByTrackerStage: Record<TrackerStageName, string> = {
	onboarding: 'Onboarding',
	questions: 'Questions',
	instruction: 'Instruction',
	evidence: 'Evidence',
	documents: 'Documents',
	listing: 'Listing',
	searches: 'Searches',
	finance: 'Finance',
	contractPack: 'Contract pack',
	sourceOfFunds: 'Source of funds',
	enquiries: 'Enquiries',
	exchange: 'Exchange',
} as const;

export const iconByTrackerStepStatus: Record<TrackerStepStatus, string> = {
	completed: '🟢',
	error: '🔴',
	inProgress: '🟡',
	pending: '🟠',
	notStarted: '⚪️',
} as const;

export const labelByTrackerStepName: Record<TrackerStepName, string> = {
	seller: 'Sellers',
	property: 'Property',
	buyer: 'Buyers',
	legals: 'Legals',
	listing: 'Listing',
	exchange: 'Exchange',
};

export const labelByTrackerStepStatus: Record<TrackerStepStatus, string> = {
	completed: 'Completed',
	error: 'Error',
	inProgress: 'In Progress',
	notStarted: 'Not Started',
	pending: 'Delayed',
};

export const propertyTitleDocumentsCategories = {
	register: 'register',
	plan: 'plan',
	conveyance: 'conveyance',
	lease: 'lease',
	transfer: 'transfer',
	subCharge: 'subCharge',
	deed: 'deed',
	charge: 'charge',
	other: 'other',
	abstract: 'abstract',
	agreement: 'agreement',
	assent: 'assent',
	assignment: 'assignment',
	indenture: 'indenture',
	licence: 'licence',
	commonholdCommunityStatement: 'commonholdCommunityStatement',
	memorandumAndArticlesOfAssociation: 'memorandumAndArticlesOfAssociation',
	surrenderOfDevelopmentRight: 'surrenderOfDevelopmentRight',
	terminationDocument: 'terminationDocument',
} as const;

export const labelByPropertyTitleDocumentsCategories: Record<PropertyTitleCategory, string> = {
	register: 'Registers',
	plan: 'Plans',
	conveyance: 'Conveyances',
	lease: 'Leases',
	transfer: 'Transfers',
	subCharge: 'Sub-Charges',
	deed: 'Deeds',
	charge: 'Charges',
	other: 'Other',
	abstract: 'Abstracts',
	agreement: 'Agreements',
	assent: 'Assents',
	assignment: 'Assignments',
	indenture: 'Indentures',
	licence: 'Licences',
	commonholdCommunityStatement: 'Commonhold Community Statements',
	memorandumAndArticlesOfAssociation: 'Memorandum and Articles of Associations',
	surrenderOfDevelopmentRight: 'Surrender of Development Rights',
	terminationDocument: 'Termination Documents',
} as const;

export const propertyDocumentAvailabilityCodes = {
	unavailable: 'UNAVAILABLE',
	immediate: 'IMMEDIATE',
	manual: 'MANUAL',
} as const;

export const propertyTitleDocumentsCategoriesKeys = Object.keys(propertyTitleDocumentsCategories);

export const logEvent = {
	initialInvitation: 'INITIAL_INVITATION',
	referrerInvitation: 'REFERRER_INVITATION',
	resendInvitation: 'RESEND_INVITATION',
	pendingInvitation: 'PENDING_INVITATION',
	pendingVerification: 'PENDING_VERIFICATION',
	pendingTasks: 'PENDING_TASKS',
	registration: 'REGISTRATION',
	resetPassword: 'RESET_PASSWORD',
	titleExtraction: 'TITLE_EXTRACTION',
	createUserBankAccounts: 'CREATE_USER_BANK_ACCOUNTS',
	verificationIssue: 'VERIFICATION_ISSUE',
	titleSelection: 'TITLE_SELECTION',
	newProperty: 'NEW_PROPERTY',
	newTransaction: 'NEW_TRANSACTION',
	invitation: 'INVITATION',
	principalPath: 'PRINCIPAL_PATH',
	changeEmailRequest: 'CHANGE_EMAIL_REQUEST',
	newUser: 'NEW_USER',
	verification: 'VERIFICATION',
	sellSideParticipantsOnboarded: 'SELL_SIDE_PARTICIPANTS_ONBOARDED',
	sellerClientCare: 'SELLER_CLIENT_CARE',
	buyerClientCare: 'BUYER_CLIENT_CARE',
	sellPackHandover: 'SELL_PACK_HANDOVER',
	kickOff: 'KICK_OFF',
	sellSideComplete: 'SELL_SIDE_COMPLETE',
	buySideComplete: 'BUY_SIDE_COMPLETE',
	buySideParticipantsOnboarded: 'BUY_SIDE_PARTICIPANTS_ONBOARDED',
	pendingSellerQuestions: 'PENDING_SELLER_QUESTIONS',
	pendingBuyerQuestions: 'PENDING_BUYER_QUESTIONS',
	pendingVerificationConfirmation: 'PENDING_VERIFICATION_CONFIRMATION',
	pendingGiftorQuestions: 'PENDING_GIFTOR_QUESTIONS',
	pendingPersonalQuestionnaire: 'PENDING_PERSONAL_QUESTIONNAIRE',
	automation: 'AUTOMATION',
	manualTrigger: 'MANUAL_TRIGGER',
	userWelcome: 'USER_WELCOME',
	userFeaturesIntroduction: 'USER_FEATURES_INTRODUCTION',
	userSuccessStories: 'USER_SUCCESS_STORIES',
	userEncouragementToAction: 'USER_ENCOURAGEMENT_TO_ACTION',
	userExclusiveTips: 'USER_EXCLUSIVE_TIPS',
	userFinalCheckIn: 'USER_FINAL_CHECK_IN',
	getVerified: 'GET_VERIFIED',
	confirmVerificationResults: 'CONFIRM_VERIFICATION_RESULTS',
	enterPersonalDetails: 'ENTER_PERSONAL_DETAILS',
	completeSellQuestions: 'COMPLETE_SELL_QUESTIONS',
	completeBuyQuestions: 'COMPLETE_BUY_QUESTIONS',
	completePropertyQuestions: 'COMPLETE_PROPERTY_QUESTIONS',
	completeGiftQuestions: 'COMPLETE_GIFT_QUESTIONS',
	uploadGiftProofOfFunds: 'UPLOAD_GIFT_PROOF_OF_FUNDS',
	uploadFloorPlan: 'UPLOAD_FLOOR_PLAN',
	uploadProtectedBuilding: 'UPLOAD_PROTECTED_BUILDING',
	uploadProtectedTrees: 'UPLOAD_PROTECTED_TREES',
	uploadAlterationsUse: 'UPLOAD_ALTERATIONS_USE',
	uploadAlterationsWindows: 'UPLOAD_ALTERATIONS_WINDOWS',
	uploadAlterationsExtension: 'UPLOAD_ALTERATIONS_EXTENSION',
	uploadAlterationsConservatory: 'UPLOAD_ALTERATIONS_CONSERVATORY',
	uploadLoftConversion: 'UPLOAD_LOFT_CONVERSION',
	uploadGarageConversion: 'UPLOAD_GARAGE_CONVERSION',
	uploadInternalWalls: 'UPLOAD_INTERNAL_WALLS',
	uploadOtherWorks: 'UPLOAD_OTHER_WORKS',
	uploadHomeWarranty: 'UPLOAD_HOME_WARRANTY',
	uploadUnderpinningWarranty: 'UPLOAD_UNDERPINNING_WARRANTY',
	uploadCentralHeatingWarranty: 'UPLOAD_CENTRAL_HEATING_WARRANTY',
	uploadRoofingWarranty: 'UPLOAD_ROOFING_WARRANTY',
	uploadElectricalWorkWarranty: 'UPLOAD_ELECTRICAL_WORK_WARRANTY',
	uploadWindowsWarranty: 'UPLOAD_WINDOWS_WARRANTY',
	uploadTimberTreatmentWarranty: 'UPLOAD_TIMBER_TREATMENT_WARRANTY',
	uploadDampRoofingWarranty: 'UPLOAD_DAMP_ROOFING_WARRANTY',
	uploadOtherWarranties: 'UPLOAD_OTHER_WARRANTIES',
	uploadBuildingInsurance: 'UPLOAD_BUILDING_INSURANCE',
	uploadHomeInsurance: 'UPLOAD_HOME_INSURANCE',
	uploadJapaneseKnotweed: 'UPLOAD_JAPANESE_KNOTWEED',
	uploadSellBankStatements: 'UPLOAD_SELL_BANK_STATEMENTS',
	uploadSellMortgageStatement: 'UPLOAD_SELL_MORTGAGE_STATEMENT',
	orderManagementPack: 'ORDER_MANAGEMENT_PACK',
	uploadGroundRent: 'UPLOAD_GROUND_RENT',
	uploadServiceCharge: 'UPLOAD_SERVICE_CHARGE',
	uploadFreeholdManagementCompany: 'UPLOAD_FREEHOLD_MANAGEMENT_COMPANY',
	uploadSewerage: 'UPLOAD_SEWERAGE',
	uploadPartyWall: 'UPLOAD_PARTY_WALL',
	uploadEICR: 'UPLOAD_EICR',
	uploadGasSafeRegister: 'UPLOAD_GAS_SAFE_REGISTER',
	uploadNoticeOfSale: 'UPLOAD_NOTICE_OF_SALE',
	uploadNoticeAboutBuilding: 'UPLOAD_NOTICE_ABOUT_BUILDING',
	uploadLeaseTermChanges: 'UPLOAD_LEASE_TERM_CHANGES',
	uploadLandlordResponseToNotice: 'UPLOAD_LANDLORD_RESPONSE_TO_NOTICE',
	uploadBuyMortgage: 'UPLOAD_BUY_MORTGAGE',
	uploadBuyRemortgage: 'UPLOAD_BUY_REMORTGAGE',
	uploadBuyEquityRelease: 'UPLOAD_BUY_EQUITY_RELEASE',
	uploadBuySavings: 'UPLOAD_BUY_SAVINGS',
	uploadBuyInheritance: 'UPLOAD_BUY_INHERITANCE',
	uploadBuyPropertySale: 'UPLOAD_BUY_PROPERTY_SALE',
	uploadBuyPension: 'UPLOAD_BUY_PENSION',
	uploadBuyGambling: 'UPLOAD_BUY_GAMBLING',
	uploadBuyCompensation: 'UPLOAD_BUY_COMPENSATION',
	uploadBuyInvestment: 'UPLOAD_BUY_INVESTMENT',
	uploadBuyBankStatements: 'UPLOAD_BUY_BANK_STATEMENTS',
	obtainNewEPC: 'OBTAIN_NEW_EPC',
	uploadTenantsNotice: 'UPLOAD_TENANTS_NOTICE',
	uploadCollectivePurchaseNotice: 'UPLOAD_COLLECTIVE_PURCHASE_NOTICE',
	uploadUserBankAccountStatements: 'UPLOAD_USER_BANK_ACCOUNT_STATEMENTS',
	uploadSection42Notice: 'UPLOAD_SECTION_42_NOTICE',
	uploadTenantsResponse: 'UPLOAD_TENANTS_RESPONSE',
	uploadCollectivePurchaseResponse: 'UPLOAD_COLLECTIVE_PURCHASE_RESPONSE',
	uploadSection42Response: 'UPLOAD_SECTION_42_RESPONSE',
	uploadGiftLoan: 'UPLOAD_GIFT_LOAN',
	uploadGiftRemortgage: 'UPLOAD_GIFT_REMORTGAGE',
	uploadGiftEquityRelease: 'UPLOAD_GIFT_EQUITY_RELEASE',
	uploadGiftSavings: 'UPLOAD_GIFT_SAVINGS',
	uploadGiftInheritance: 'UPLOAD_GIFT_INHERITANCE',
	uploadGiftPropertySale: 'UPLOAD_GIFT_PROPERTY_SALE',
	uploadGiftPension: 'UPLOAD_GIFT_PENSION',
	uploadGiftGambling: 'UPLOAD_GIFT_GAMBLING',
	uploadGiftCompensation: 'UPLOAD_GIFT_COMPENSATION',
	uploadGiftInvestment: 'UPLOAD_GIFT_INVESTMENT',
	titlePendingApplications: 'TITLE_PENDING_APPLICATIONS',
	assignTitle: 'ASSIGN_TITLE',
	confirmRelationship: 'CONFIRM_RELATIONSHIP',
	confirmBuyTeam: 'CONFIRM_BUY_TEAM',
	downloadTitle: 'DOWNLOAD_TITLE',
	uploadGiftBankStatements: 'UPLOAD_GIFT_BANK_STATEMENTS',
	uploadElectricalSafetyCertificate: 'UPLOAD_ELECTRICAL_SAFETY_CERTIFICATE',
	uploadElectricalBuildingCompliance: 'UPLOAD_ELECTRICAL_BUILDING_COMPLIANCE',
	uploadElectricalBuildingCompletion: 'UPLOAD_ELECTRICAL_BUILDING_COMPLETION',
	uploadElectricalInsurance: 'UPLOAD_ELECTRICAL_INSURANCE',
	uploadRadonTest: 'UPLOAD_RADON_TEST',
	uploadFloodReport: 'UPLOAD_FLOOD_REPORT',
	reviewUserBankAccounts: 'REVIEW_USER_BANK_ACCOUNTS',
	uploadSolarPanelsPlanningConsent: 'UPLOAD_SOLAR_PANELS_PLANNING_CONSENT',
	uploadSolarPanelsGuaranteesAndWarranties: 'UPLOAD_SOLAR_PANELS_GUARANTEES_AND_WARRANTIES',
	uploadSolarPanelsPanelsLease: 'UPLOAD_SOLAR_PANELS_PANELS_LEASE',
	uploadSolarPanelsInstallationOrComplianceCertificates: 'UPLOAD_SOLAR_PANELS_INSTALLATION_OR_COMPLIANCE_CERTIFICATES',
	uploadSolarPanelsRoofSurvey: 'UPLOAD_SOLAR_PANELS_ROOF_SURVEY',
	orderBuySearches: 'ORDER_BUY_SEARCHES',
	orderSalePack: 'ORDER_SALE_PACK',
	orderSellSearches: 'ORDER_SELL_SEARCHES',
	uploadNameChangeEvidence: 'UPLOAD_NAME_CHANGE_EVIDENCE',
	signGiftDeclaration: 'SIGN_GIFT_DECLARATION',
	signPropertyForms: 'SIGN_PROPERTY_FORMS',
	signContentsForm: 'SIGN_CONTENTS_FORM',
	viewPropertyForms: 'VIEW_PROPERTY_FORMS',
	viewContentsForm: 'VIEW_CONTENTS_FORM',
	sellQuoteSent: 'SELL_QUOTE_SENT',
	sellQuoteAccepted: 'SELL_QUOTE_ACCEPTED',
	searchesUploaded: 'SEARCHES_UPLOADED',
	searchesOrdered: 'SEARCHES_ORDERED',
	sellSolicitorAssigned: 'SELL_SOLICITOR_ASSIGNED',
	sellCCLSent: 'SELL_CCL_SENT',
	sellCCLSigned: 'SELL_CCL_SIGNED',
	sellPriceSet: 'SELL_PRICE_SET',
	sellPropertyListed: 'SELL_PROPERTY_LISTED',
	sellOfferAccepted: 'SELL_OFFER_ACCEPTED',
	buyQuoteSent: 'BUY_QUOTE_SENT',
	buyQuoteAccepted: 'BUY_QUOTE_ACCEPTED',
	acceptInvite: 'ACCEPT_INVITE',
	buySolicitorAssigned: 'BUY_SOLICITOR_ASSIGNED',
	buyCCLSent: 'BUY_CCL_SENT',
	buyCCLSigned: 'BUY_CCL_SIGNED',
	sellContractSent: 'SELL_CONTRACT_SENT',
	buyContractApproved: 'BUY_CONTRACT_APPROVED',
	buyTR1Sent: 'BUY_TR1_SENT',
	buyContractReceived: 'BUY_CONTRACT_RECEIVED',
	sellTR1Received: 'SELL_TR1_RECEIVED',
	sellTR1Approved: 'SELL_TR1_APPROVED',
	buyEnquiriesSent: 'BUY_ENQUIRIES_SENT',
	sellEnquiriesReceived: 'SELL_ENQUIRIES_RECEIVED',
	sellRepliesSent: 'SELL_REPLIES_SENT',
	buyRepliesReceived: 'BUY_REPLIES_RECEIVED',
	buyEnquiriesComplete: 'BUY_ENQUIRIES_COMPLETE',
	exchangeDateAgreed: 'EXCHANGE_DATE_AGREED',
	completionDateAgreed: 'COMPLETION_DATE_AGREED',
	hasExchanged: 'HAS_EXCHANGED',
	hasCompleted: 'HAS_COMPLETED',
	userResetVerification: 'USER_RESET_VERIFICATION',
	verificationUpdate: 'VERIFICATION_UPDATE',
	propertyOnboardingNotification: 'PROPERTY_ONBOARDING_NOTIFICATION',
	transactionOnboardingNotification: 'TRANSACTION_ONBOARDING_NOTIFICATION',
	newCompany: 'NEW_COMPANY',
	approvedCompany: 'APPROVED_COMPANY',
	rejectedCompany: 'REJECTED_COMPANY',
	approvedBranch: 'APPROVED_BRANCH',
	rejectedBranch: 'REJECTED_BRANCH',
	newBranch: 'NEW_BRANCH',
	companyHelp: 'COMPANY_HELP',
	branchHelp: 'BRANCH_HELP',
	newTransactionHandler: 'NEW_TRANSACTION_HANDLER',
	searchesUploadedNotification: 'SEARCHES_UPLOADED_NOTIFICATION',
	uploadMemorandumOfSale: 'UPLOAD_MEMORANDUM_OF_SALE',
	uploadMemorandumOfPurchase: 'UPLOAD_MEMORANDUM_OF_PURCHASE',
	saleWithdrawal: 'SALE_WITHDRAWAL',
	uploadDeedOfTrust: 'UPLOAD_DEED_OF_TRUST',
	paymentReceived: 'PAYMENT_RECEIVED',
	missingQuoteAddress: 'MISSING_QUOTE_ADDRESS',
	quoteAccepted: 'QUOTE_ACCEPTED',
	quoteMessage: 'QUOTE_MESSAGE',
	quoteCallbackRequest: 'QUOTE_CALLBACK_REQUEST',
	uploadTenancyAgreement: 'UPLOAD_TENANCY_AGREEMENT',
	uploadHMOHealthAndSafety: 'UPLOAD_HMO_HEALTH_AND_SAFETY',
	uploadHMOSmokeAlarmsAndFireDoors: 'UPLOAD_HMO_SMOKE_ALARMS_AND_FIRE_DOORS',
	cclRequiredNotification: 'CCL_REQUIRED_NOTIFICATION',
	cclUploadRequiredNotification: 'CCL_UPLOAD_REQUIRED_NOTIFICATION',
	buyCclRequiredNotification: 'BUY_CCL_REQUIRED_NOTIFICATION',
	buyCclUploadRequiredNotification: 'BUY_CCL_UPLOAD_REQUIRED_NOTIFICATION',
	invitationAccepted: 'INVITATION_ACCEPTED',
	sellSearchesAvailable: 'SELL_SEARCHES_AVAILABLE',
	uploadContractPackNotification: 'UPLOAD_CONTRACT_PACK_NOTIFICATION',
	contractIssuedBuyNotification: 'CONTRACT_ISSUED_BUY_NOTIFICATION',
	contractIssuedSellNotification: 'CONTRACT_ISSUED_SELL_NOTIFICATION',
	contractsApprovedNotification: 'CONTRACTS_APPROVED_NOTIFICATION',
	updateSellContractsApprovedNotification: 'UPDATE_SELL_CONTRACTS_APPROVED_NOTIFICATION',
	updateBuyContractsApprovedNotification: 'UPDATE_BUY_CONTRACTS_APPROVED_NOTIFICATION',
	enquiriesRaisedNotification: 'ENQUIRIES_RAISED_NOTIFICATION',
	enquiriesAnsweredNotification: 'ENQUIRIES_ANSWERED_NOTIFICATION',
	enquiriesSettledNotification: 'ENQUIRIES_SETTLED_NOTIFICATION',
	updateSellEnquiriesSettledNotification: 'UPDATE_SELL_ENQUIRIES_SETTLED_NOTIFICATION',
	updateBuyEnquiriesSettledNotification: 'UPDATE_BUY_ENQUIRIES_SETTLED_NOTIFICATION',
	agreeCompletionDateNotification: 'AGREE_COMPLETION_DATE_NOTIFICATION',
	exchangeCongratulationsNotification: 'EXCHANGE_CONGRATULATIONS_NOTIFICATION',
} as const;

export const labelByLogEvent: Record<LogEvent, string> = {
	CHANGE_EMAIL_REQUEST: 'Change Email Request',
	INITIAL_INVITATION: 'Initial Transaction Invitation',
	INVITATION: 'Invitation',
	NEW_PROPERTY: 'New Property',
	NEW_TRANSACTION: 'New Transaction',
	NEW_USER: 'New User',
	PENDING_INVITATION: 'Pending Invitation',
	PRINCIPAL_PATH: 'Principal Path',
	REGISTRATION: 'Registration',
	RESEND_INVITATION: 'Resend Invitation',
	RESET_PASSWORD: 'Reset Password',
	TITLE_EXTRACTION: 'Title Extraction',
	TITLE_SELECTION: 'Title Selection',
	VERIFICATION: 'Verification',
	PENDING_VERIFICATION: 'Pending Verification',
	SELL_SIDE_PARTICIPANTS_ONBOARDED: 'All sell-side parties onboarded',
	SELL_PACK_HANDOVER: 'Sell Pack Handover',
	KICK_OFF: 'Buy-side Kick-off',
	SELL_SIDE_COMPLETE: 'Sell Side Complete',
	BUY_SIDE_COMPLETE: 'Buy Side Complete',
	PENDING_PERSONAL_QUESTIONNAIRE: 'Pending Personal Details',
	PENDING_SELLER_QUESTIONS: 'Pending Seller Questions',
	PENDING_BUYER_QUESTIONS: 'Pending Buyer Questions',
	PENDING_GIFTOR_QUESTIONS: 'Pending Giftor Questions',
	AUTOMATION: 'Automation',
	MANUAL_TRIGGER: 'Manual Trigger',
	BUY_SIDE_PARTICIPANTS_ONBOARDED: 'All buy-side parties onboarded',
	// BUYER: transactionDocuments.buyer.name,
	CONTRACT_PACK_SENT: transactionDocuments.contractPackSent.name,
	CYBERCRIME_NOTICE: transactionDocuments.cyberCrimeNotice.name,
	DEEDS_REQUEST: transactionDocuments.deedsRequest.name,
	ADVANCE_ENQUIRIES: transactionDocuments.advanceEnquiries.name,
	FILE_CLOSING_NOTICE: transactionDocuments.fileClosingNotice.name,
	GIFT_DEED: transactionDocuments.giftDeed.name,
	MEMORANDUM_OF_SALE: transactionDocuments.memorandumOfSale.name,
	REPLIES_TO_COMPLETION_INFO: transactionDocuments.repliesToCompletionInfo.name,
	REPLIES_TO_ENQUIRIES: transactionDocuments.repliesToEnquiries.name,
	CIF_BUYERS: transactionDocuments.CIFBuyers.name,
	CIF_SELLERS: transactionDocuments.CIFSellers.name,
	SDLT: transactionDocuments.SDLT.name,
	STANDARD_CONDITIONS_OF_SALE: transactionDocuments.standardConditionsOfSale.name,
	TA10: transactionDocuments.ta10.name,
	TA6: transactionDocuments.ta6.name,
	TA7: transactionDocuments.ta7.name,
	TR1: transactionDocuments.tr1.name,
	TR13: transactionDocuments.tr13.name,
	GET_VERIFIED: 'Get verified',
	CONFIRM_VERIFICATION_RESULTS: 'Confirm verification results',
	ENTER_PERSONAL_DETAILS: 'Enter personal details',
	ORDER_BUY_SEARCHES: 'Order searches (buy side)',
	ORDER_SELL_SEARCHES: 'Order searches (sell side)',
	COMPLETE_SELL_QUESTIONS: 'Complete sell questions',
	COMPLETE_BUY_QUESTIONS: 'Complete buy questions',
	UPLOAD_GIFT_PROOF_OF_FUNDS: 'Upload buyer gift proof of funds',
	UPLOAD_FLOOR_PLAN: 'Upload floor plan',
	UPLOAD_PROTECTED_BUILDING: 'Upload protected building',
	UPLOAD_PROTECTED_TREES: 'Upload protected trees',
	UPLOAD_ALTERATIONS_USE: 'Upload alterations use',
	UPLOAD_ALTERATIONS_WINDOWS: 'Upload alterations windows',
	UPLOAD_ALTERATIONS_EXTENSION: 'Upload alterations extension',
	UPLOAD_ALTERATIONS_CONSERVATORY: 'Upload alterations conservatory',
	UPLOAD_LOFT_CONVERSION: 'Upload loft conversion',
	UPLOAD_GARAGE_CONVERSION: 'Upload garage conversion',
	UPLOAD_INTERNAL_WALLS: 'Upload internal walls',
	UPLOAD_OTHER_WORKS: 'Upload other works',
	UPLOAD_HOME_WARRANTY: 'Upload home warranty',
	UPLOAD_UNDERPINNING_WARRANTY: 'Upload underpinning warranty',
	UPLOAD_CENTRAL_HEATING_WARRANTY: 'Upload central heating warranty',
	UPLOAD_ROOFING_WARRANTY: 'Upload roofing warranty',
	UPLOAD_ELECTRICAL_WORK_WARRANTY: 'Upload electrical work warranty',
	UPLOAD_WINDOWS_WARRANTY: 'Upload windows warranty',
	UPLOAD_TIMBER_TREATMENT_WARRANTY: 'Upload timber treatment warranty',
	UPLOAD_DAMP_ROOFING_WARRANTY: 'Upload damp roofing warranty',
	UPLOAD_OTHER_WARRANTIES: 'Upload other warranties',
	UPLOAD_BUILDING_INSURANCE: 'Upload building insurance',
	UPLOAD_HOME_INSURANCE: 'Upload home insurance',
	UPLOAD_JAPANESE_KNOTWEED: 'Upload japanese knotweed',
	UPLOAD_SELL_BANK_STATEMENTS: 'Upload sell bank statements',
	UPLOAD_SELL_MORTGAGE_STATEMENT: 'Upload sell mortgage statement',
	ORDER_MANAGEMENT_PACK: 'Order management pack',
	UPLOAD_GROUND_RENT: 'Upload charge rent',
	UPLOAD_SERVICE_CHARGE: 'Upload service charge',
	UPLOAD_FREEHOLD_MANAGEMENT_COMPANY: 'Upload freehold management company',
	UPLOAD_SEWERAGE: 'Upload sewerage',
	UPLOAD_PARTY_WALL: 'Upload party wall',
	UPLOAD_EICR: 'Upload EICR',
	UPLOAD_GAS_SAFE_REGISTER: 'Upload gas safety register',
	UPLOAD_NOTICE_OF_SALE: 'Upload notice of sale',
	UPLOAD_NOTICE_ABOUT_BUILDING: 'Upload notice about building',
	UPLOAD_LEASE_TERM_CHANGES: 'Upload lease term changes',
	UPLOAD_COLLECTIVE_PURCHASE_NOTICE: 'Upload collective purchase notice',
	UPLOAD_LANDLORD_RESPONSE_TO_NOTICE: 'Upload landlord response to notice',
	UPLOAD_BUY_MORTGAGE: 'Upload buy mortgage',
	UPLOAD_BUY_REMORTGAGE: 'Upload buy remortgage',
	UPLOAD_BUY_EQUITY_RELEASE: 'Upload buy equity release',
	UPLOAD_BUY_SAVINGS: 'Upload buy savings',
	UPLOAD_BUY_INHERITANCE: 'Upload buy inheritance',
	UPLOAD_BUY_PROPERTY_SALE: 'Upload buy property sale',
	UPLOAD_BUY_PENSION: 'Upload buy pension',
	UPLOAD_BUY_GAMBLING: 'Upload buy gambling',
	UPLOAD_BUY_COMPENSATION: 'Upload buy compensation',
	UPLOAD_BUY_INVESTMENT: 'Upload buy investment',
	UPLOAD_BUY_BANK_STATEMENTS: 'Upload buy bank statements',
	SELLER_CLIENT_CARE: 'Seller client care',
	BUYER_CLIENT_CARE: 'Buyer client care',
	OBTAIN_NEW_EPC: 'Obtain new EPC',
	TITLE_PENDING_APPLICATIONS: 'Title pending applications',
	UPLOAD_GIFT_COMPENSATION: 'Upload gift compensation',
	UPLOAD_GIFT_INVESTMENT: 'Upload gift investment',
	UPLOAD_GIFT_EQUITY_RELEASE: 'Upload gift equity release',
	UPLOAD_GIFT_GAMBLING: 'Upload gift gambling',
	UPLOAD_GIFT_INHERITANCE: 'Upload gift inheritance',
	UPLOAD_GIFT_LOAN: 'Upload gift mortgage',
	UPLOAD_GIFT_PENSION: 'Upload gift pension',
	UPLOAD_GIFT_PROPERTY_SALE: 'Upload gift property sale',
	UPLOAD_GIFT_REMORTGAGE: 'Upload gift remortgage',
	UPLOAD_GIFT_SAVINGS: 'Upload gift savings',
	UPLOAD_ELECTRICAL_BUILDING_COMPLIANCE: 'Electrical installation: Upload Building Regulations Compliance Certificate',
	UPLOAD_ELECTRICAL_SAFETY_CERTIFICATE: 'Electrical installation: Upload electrical safety certificate',
	UPLOAD_ELECTRICAL_INSURANCE: 'Electrical installation: Upload no-certificate insurance',
	UPLOAD_NAME_CHANGE_EVIDENCE: 'Upload name change evidence (ID vs Title)',
	VERIFICATION_ISSUE: 'Verification Issue',
	PENDING_VERIFICATION_CONFIRMATION: 'Pending Verification Confirmation',
	USER_WELCOME: 'User Welcome',
	USER_FEATURES_INTRODUCTION: 'User Features Introduction',
	USER_SUCCESS_STORIES: 'User Success Stories',
	USER_ENCOURAGEMENT_TO_ACTION: 'User Encouragement To Action',
	USER_EXCLUSIVE_TIPS: 'User Exclusive Tips',
	USER_FINAL_CHECK_IN: 'User Final Check In',
	UPLOAD_TENANTS_NOTICE: 'Upload Tenants Notice',
	UPLOAD_SECTION_42_NOTICE: 'Upload Section 42 Notice',
	UPLOAD_TENANTS_RESPONSE: 'Upload Tenants Response',
	UPLOAD_COLLECTIVE_PURCHASE_RESPONSE: 'Upload Collective Purchase Response',
	UPLOAD_SECTION_42_RESPONSE: 'Upload Section 42 Response',
	UPLOAD_GIFT_BANK_STATEMENTS: 'Upload Gift Bank Statements',
	UPLOAD_ELECTRICAL_BUILDING_COMPLETION: 'Upload Electrical Building Completion',
	SELL_QUOTE_SENT: 'Sell Quote Sent',
	SELL_QUOTE_ACCEPTED: 'Sell Quote Accepted',
	SEARCHES_UPLOADED: 'Searches Uploaded',
	SELL_SOLICITOR_ASSIGNED: 'Sell Solicitor Assigned',
	SELL_CCL_SENT: 'Sell CCL Sent',
	SELL_CCL_SIGNED: 'Sell CCL Signed',
	SELL_PROPERTY_LISTED: 'Sell Property Listed',
	SELL_OFFER_ACCEPTED: 'Sell Offer Accepted',
	SELL_PRICE_SET: 'Sell Price Set',
	BUY_QUOTE_SENT: 'Buy Quote Sent',
	BUY_QUOTE_ACCEPTED: 'Buy Quote Accepted',
	ACCEPT_INVITE: 'Accept Invite',
	BUY_SOLICITOR_ASSIGNED: 'Buy Solicitor Assigned',
	BUY_CCL_SENT: 'Buy CCL Sent',
	BUY_CCL_SIGNED: 'Buy CCL Signed',
	SELL_CONTRACT_SENT: 'Sell Contract Sent',
	BUY_CONTRACT_RECEIVED: 'Buy Contract Received',
	BUY_CONTRACT_APPROVED: 'Buy Contract Approved',
	BUY_TR1_SENT: 'Buy TR1 Sent',
	SELL_TR1_RECEIVED: 'Sell TR1 Received',
	SELL_TR1_APPROVED: 'Sell TR1 Approved',
	BUY_ENQUIRIES_SENT: 'Buy Enquiries Sent',
	SELL_ENQUIRIES_RECEIVED: 'Sell Enquiries Received',
	SELL_REPLIES_SENT: 'Sell Replies Sent',
	BUY_REPLIES_RECEIVED: 'Buy Replies Received',
	BUY_ENQUIRIES_COMPLETE: 'Buy Enquiries Complete',
	EXCHANGE_DATE_AGREED: 'Exchange Date Agreed',
	COMPLETION_DATE_AGREED: 'Completion Date Agreed',
	HAS_EXCHANGED: 'Has Exchanged',
	HAS_COMPLETED: 'Has Completed',
	PENDING_TASKS: 'Pending tasks',
	ASSIGN_TITLE: 'Assign title',
	COMPLETE_PROPERTY_QUESTIONS: 'Complete property questions',
	UPLOAD_MEMORANDUM_OF_PURCHASE: 'Upload memorandum of purchase',
	UPLOAD_MEMORANDUM_OF_SALE: 'Upload memorandum of sale',
	AGREE_COMPLETION_DATE_NOTIFICATION: 'Agree completion date notification',
	APPROVED_BRANCH: 'Approved branch',
	APPROVED_COMPANY: 'Approved company',
	BRANCH_HELP: 'Branch help',
	BUY_CCL_REQUIRED_NOTIFICATION: 'Buy ccl required notification',
	fix: 'Fix',
	BUY_CCL_UPLOAD_REQUIRED_NOTIFICATION: 'Buy ccl upload required notification',
	CCL_REQUIRED_NOTIFICATION: 'Ccl required notification',
	CCL_UPLOAD_REQUIRED_NOTIFICATION: 'Ccl upload required notification',
	COMPANY_HELP: 'Company help',
	COMPLETE_GIFT_QUESTIONS: 'Complete gift questions',
	CONFIRM_BUY_TEAM: 'Confirm buy team',
	CONFIRM_RELATIONSHIP: 'Confirm relationship',
	CONTRACT_ISSUED_BUY_NOTIFICATION: 'Contract issued buy notification',
	CONTRACT_ISSUED_SELL_NOTIFICATION: 'Contract issued sell notification',
	CONTRACTS_APPROVED_NOTIFICATION: 'Contracts approved notification',
	CREATE_USER_BANK_ACCOUNTS: 'Create user bank accounts',
	DOWNLOAD_TITLE: 'Download title',
	ENQUIRIES_ANSWERED_NOTIFICATION: 'Enquiries answered notification',
	ENQUIRIES_RAISED_NOTIFICATION: 'Enquiries raised notification',
	ENQUIRIES_SETTLED_NOTIFICATION: 'Enquiries settled notification',
	EXCHANGE_CONGRATULATIONS_NOTIFICATION: 'Exchange congratulations notification',
	INVITATION_ACCEPTED: 'Invitation accepted',
	MISSING_QUOTE_ADDRESS: 'Missing quote address',
	NEW_BRANCH: 'New branch',
	NEW_COMPANY: 'New company',
	NEW_TRANSACTION_HANDLER: 'New transaction handler',
	ORDER_SALE_PACK: 'Order sale pack',
	PAYMENT_RECEIVED: 'Payment received',
	PROPERTY_ONBOARDING_NOTIFICATION: 'Property onboarding notification',
	QUOTE_ACCEPTED: 'Quote accepted',
	QUOTE_CALLBACK_REQUEST: 'Quote callback request',
	QUOTE_MESSAGE: 'Quote message',
	REFERRER_INVITATION: 'Referrer invitation',
	REJECTED_BRANCH: 'Rejected branch',
	REJECTED_COMPANY: 'Rejected company',
	REVIEW_USER_BANK_ACCOUNTS: 'Review user bank accounts',
	SALE_WITHDRAWAL: 'Sale withdrawal',
	SEARCHES_ORDERED: 'Searches ordered',
	SEARCHES_UPLOADED_NOTIFICATION: 'Searches uploaded notification',
	SELL_SEARCHES_AVAILABLE: 'Sell searches available',
	SIGN_CONTENTS_FORM: 'Sign contents form',
	SIGN_GIFT_DECLARATION: 'Sign gift declaration',
	SIGN_PROPERTY_FORMS: 'Sign property forms',
	TRANSACTION_ONBOARDING_NOTIFICATION: 'Transaction onboarding notification',
	UPDATE_BUY_CONTRACTS_APPROVED_NOTIFICATION: 'Update buy contracts approved notification',
	UPDATE_BUY_ENQUIRIES_SETTLED_NOTIFICATION: 'Update buy enquiries settled notification',
	UPDATE_SELL_CONTRACTS_APPROVED_NOTIFICATION: 'Update sell contracts approved notification',
	UPDATE_SELL_ENQUIRIES_SETTLED_NOTIFICATION: 'Update sell enquiries settled notification',
	UPLOAD_CONTRACT_PACK_NOTIFICATION: 'Upload contract pack notification',
	UPLOAD_DEED_OF_TRUST: 'Upload deed of trust',
	UPLOAD_FLOOD_REPORT: 'Upload flood report',
	UPLOAD_HMO_HEALTH_AND_SAFETY: 'Upload hmo health and safety',
	UPLOAD_HMO_SMOKE_ALARMS_AND_FIRE_DOORS: 'Upload hmo smoke alarms and fire doors',
	UPLOAD_RADON_TEST: 'Upload radon test',
	UPLOAD_SOLAR_PANELS_GUARANTEES_AND_WARRANTIES: 'Upload solar panels guarantees and warranties',
	UPLOAD_SOLAR_PANELS_INSTALLATION_OR_COMPLIANCE_CERTIFICATES:
		'Upload solar panels installation or compliance certificates',
	UPLOAD_SOLAR_PANELS_PANELS_LEASE: 'Upload solar panels panels lease',
	UPLOAD_SOLAR_PANELS_PLANNING_CONSENT: 'Upload solar panels planning consent',
	UPLOAD_SOLAR_PANELS_ROOF_SURVEY: 'Upload solar panels roof survey',
	UPLOAD_TENANCY_AGREEMENT: 'Upload tenancy agreement',
	UPLOAD_USER_BANK_ACCOUNT_STATEMENTS: 'Upload user bank account statements',
	USER_RESET_VERIFICATION: 'User reset verification',
	VERIFICATION_UPDATE: 'Verification update',
	VIEW_CONTENTS_FORM: 'View contents form',
	VIEW_PROPERTY_FORMS: 'View property forms',
};

export const labelByLogStatus: Record<LogStatusEnum, string> = {
	FAILED: 'Failed',
	PENDING: 'Pending',
	SUCCEED: 'Succeed',
	SENT: 'Sent',
	COMPLETE: 'Complete',
	CREATED: 'Created',
	DELETED: 'Deleted',
};

export const labelByLogType: Record<LogTypeEnum, string> = { EMAIL: 'Email', JOB: 'Job', TASK: 'Task' };
